import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { Z_CLASS } from '@/lib/layers';

/**
 * tailwind-merge's built-in `z-index` group only matches `z-auto`, `z-<number>`
 * and arbitrary values. Our bands (`z-overlay`, `z-tooltip`, … — see
 * `src/lib/layers.ts`) are named theme keys, so out of the box twMerge sees two
 * UNRELATED classes and keeps BOTH:
 *
 *   twMerge('z-overlay', 'z-tooltip')  →  'z-overlay z-tooltip'
 *
 * Two rules of equal specificity, so the winner is whichever Tailwind emitted
 * later in the stylesheet — not the one written last at the call site. Every
 * `className="z-tooltip"` override passed into a component whose base class
 * already carries a band would be silently ignored, which is exactly the bug
 * that shipped in the main client app (see FIX(z-override) in
 * `client/src/lib/utils.ts`).
 *
 * Registering the bands as one class group makes them conflict properly: last
 * one wins, in call order, like every other utility. New bands added to
 * `Z_CLASS` are picked up automatically.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: { z: [{ z: Object.values(Z_CLASS).map((c) => c.slice('z-'.length)) }] },
  },
});

/** Merge Tailwind classes with conflict resolution. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
